/**
 * Types for the "Report this AI content" flow.
 *
 * Lets a signed-in user flag an AI-generated artefact (currently the home
 * summary) as inappropriate, inaccurate, or offensive. Required by Google
 * Play's Generative AI policy: every surface that shows model output must
 * offer an in-app way to report it.
 */

/**
 * The kind of AI artefact being reported. 'general' is a context-free report
 * raised from Settings (no specific artefact attached); extend with concrete
 * surfaces as they ship.
 */
export type ReportedContentType =
  | 'home-summary'
  | 'chat'
  | 'ai-suggestion'
  | 'general';

/** Caps shared by the client and server Zod schema so both agree on limits. */
export const CONTENT_REPORT_LIMITS = {
  REASON_MAX_CHARS: 1000,
  SNAPSHOT_MAX_CHARS: 4000,
} as const;

/** Payload POSTed to /api/report-ai-content. */
export type ContentReportInput = {
  contentType: ReportedContentType;
  /** Stable id of the reported artefact (e.g. HomeSummary.id). */
  contentId: string;
  /** Optional free-text explanation from the reporter. */
  reason?: string | null;
  /** A trimmed copy of the offending text so the operator can triage. */
  contentSnapshot: string;
  /** Optional metadata for context (variant, generation timestamp). */
  variant?: string | null;
  generatedAt?: string | null;
};

export type ContentReportResult =
  | { success: true }
  | { success: false; error: string };

/**
 * A reportable AI artefact stashed by the source surface (e.g. the home
 * summary card) just before navigating to the report screen, so the screen
 * can pre-attach the exact content without threading it through nav params.
 */
export type PendingReportContext = {
  contentType: ReportedContentType;
  contentId: string;
  variant: string | null;
  generatedAt: string | null;
  snapshot: string;
};
